/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { register } from '../registry.js';

/**
 * A video that downloads nothing until somebody presses play.
 *
 * `preload="none"` and a poster frame is the whole idea: a partner's reel is a megabyte or two,
 * and the `autoplay muted loop` video this replaces paid that on every visit whether anybody
 * watched or not. What it costs is a first press that does nothing by itself, since a `<video>`
 * with no controls and no autoplay has no way in -- hence the button over the middle of it.
 *
 * Which is why this file reveals the overlay rather than hiding it. The markup ships the video
 * with its own `controls` and the overlay `hidden`, so a page with no script has a video that
 * plays, drawn by the browser; the script takes the controls off and puts the button on, and from
 * then on the controls come back the moment the file is actually wanted. A component whose markup
 * needed script to be usable would be the one kind this library does not have.
 */
function videoOf(element: Element): HTMLVideoElement | null {
    const video = element.querySelector('video');

    return video instanceof HTMLVideoElement ? video : null;
}

function overlayOf(element: Element): HTMLElement[] {
    return [element.querySelector('.wp-video-poster-play'), element.querySelector('.wp-video-poster-scrim')].filter(
        (part): part is HTMLElement => part instanceof HTMLElement
    );
}

/**
 * Hands one poster its overlay, which is also what takes the browser's own controls off it.
 *
 * A frame already playing is left alone: a repaint after a Turbo swap or a `wp:paint` must not put
 * a play button back over a film somebody is watching.
 */
export function paintPoster(element: Element): void {
    const video = videoOf(element);
    const play = element.querySelector('.wp-video-poster-play');

    if (video === null || !(play instanceof HTMLElement) || element.getAttribute('data-wp-playing') === 'true') {
        return;
    }

    video.controls = false;
    overlayOf(element).forEach((part) => {
        part.hidden = false;
    });
}

export function paint(root: ParentNode): void {
    root.querySelectorAll('.wp-video-poster').forEach(paintPoster);
}

export function listen(): void {
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const control = target.closest('.wp-video-poster-play');
        const element = control?.closest('.wp-video-poster');
        if (control === null || element == null) {
            return;
        }

        const video = videoOf(element);
        if (video === null) {
            return;
        }

        /* The overlay goes first, so the press has a visible effect before the network does
           anything: `preload="none"` means the first frames are being fetched now. */
        element.setAttribute('data-wp-playing', 'true');
        overlayOf(element).forEach((part) => {
            part.hidden = true;
        });

        video.controls = true;

        /* `play()` rejects when the browser refuses -- no user gesture it recognises, a codec it
           cannot decode -- and an unhandled rejection in a click handler is a console error on an
           otherwise working page. The reader is left with a video wearing its own controls, which
           is exactly the no-script state and is playable by hand. */
        void video.play().catch(() => undefined);
    });

    register(paint);
}
